import { useEffect, useState, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { remote_CollectRestoreTransaction_freePhoto } from '~/common/typescript/apis/CollectSyncTransactionAPIs';
import {
  freePhotoListAtom,
  freePhotoFiltersAtom,
  freePhotoPageAtom,
  freePhotoPageSizeAtom,
  freePhotoTotalCountAtom,
  freePhotoLoadingAtom,
} from '~/stores/FreePhotoStore';

/**
 * Custom hook to manage FreePhoto list fetching and state
 */
export const useFreePhotoList = () => {
  const [photos, setPhotos] = useAtom(freePhotoListAtom);
  const [totalCount, setTotalCount] = useAtom(freePhotoTotalCountAtom);
  const [loading, setLoading] = useAtom(freePhotoLoadingAtom);
  const filters = useAtomValue(freePhotoFiltersAtom);
  const page = useAtomValue(freePhotoPageAtom);
  const pageSize = useAtomValue(freePhotoPageSizeAtom);

  const [error, setError] = useState<Error | null>(null);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Prepare request parameters
      const params: any = {
        page: page,
        pageSize: pageSize,
      };

      // Add project filter (only if specific project selected, not 0 which means "all")
      if (filters.projectId !== undefined && filters.projectId > 0) {
        params.projectId = filters.projectId;
      }

      const response = await remote_CollectRestoreTransaction_freePhoto(params);

      // Apply client-side filtering if needed
      let filteredPhotos = response.results || [];

      // Filter by location query (client-side)
      if (filters.locationQuery) {
        const query = filters.locationQuery.toLowerCase();
        filteredPhotos = filteredPhotos.filter((photo) => {
          try {
            const metadata = JSON.parse(photo.metadata);
            const address = metadata.address || '';
            return address.toLowerCase().includes(query);
          } catch {
            return false;
          }
        });
      }

      // Filter by date range (client-side)
      if (filters.dateRange && filters.dateRange.length === 2) {
        const [startDate, endDate] = filters.dateRange;
        filteredPhotos = filteredPhotos.filter((photo) => {
          if (!photo.created_at) return false;
          const photoDate = new Date(photo.created_at);
          return photoDate >= new Date(startDate) && photoDate <= new Date(endDate);
        });
      }

      // Apply sorting (client-side)
      filteredPhotos = [...filteredPhotos].sort((a, b) => {
        switch (filters.sortBy) {
          case 'date_desc':
            return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
          case 'date_asc':
            return new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
          case 'name_asc':
            return a.file_name.localeCompare(b.file_name);
          case 'name_desc':
            return b.file_name.localeCompare(a.file_name);
          default:
            return 0;
        }
      });

      setPhotos(filteredPhotos);
      setTotalCount(response.pagination?.total || filteredPhotos.length);
    } catch (err) {
      console.error('Failed to fetch photos:', err);
      setError(err as Error);
      setPhotos([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize, setPhotos, setTotalCount, setLoading]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  return {
    photos,
    loading,
    error,
    refetch: fetchPhotos,
    totalCount,
  };
};